import React, { createContext, useContext, useEffect, useState } from 'react'
import { 
  User as FirebaseUser,
  signInWithPopup,
  GoogleAuthProvider,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  sendEmailVerification
} from 'firebase/auth'
import { auth } from '../lib/firebase'
import { User, Staff } from '../types'

interface AuthContextType {
  user: User | null
  staff: Staff | null
  firebaseUser: FirebaseUser | null
  loading: boolean
  signUp: (userData: { name: string; email: string; phone?: string; optInMarketing?: boolean; password: string }) => Promise<void>
  signIn: (email: string, password: string) => Promise<void>
  signInWithGoogle: () => Promise<void>
  signOut: () => Promise<void>
  staffSignIn: (username: string, password: string) => Promise<void>
  resetPassword: (email: string) => Promise<void>
  sendEmailVerification: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [staff, setStaff] = useState<Staff | null>(null)
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null)
  const [loading, setLoading] = useState(true)
  const [isSigningUp, setIsSigningUp] = useState(false) // Track signup state

  // CRITICAL: Check for existing staff session IMMEDIATELY on mount
  // This must run before Firebase auth to prevent redirect loops
  useEffect(() => {
    const checkStaffSession = () => {
      const staffSession = localStorage.getItem('staffSession')
      if (staffSession) {
        try {
          const { staff: staffData } = JSON.parse(staffSession)
          console.log('AuthContext: Loading staff session from localStorage:', staffData)
          setStaff(staffData)
          setUser(null) // Clear user if staff is logged in
          setLoading(false) // CRITICAL: Set loading to false immediately
          return true
        } catch (error) {
          console.error('Error loading staff session:', error)
          localStorage.removeItem('staffSession')
        }
      }
      return false
    }
    
    // Check immediately on mount
    checkStaffSession()
  }, [])

  // Helper function to fetch user profile with retries
  const fetchUserProfile = async (firebaseUser: FirebaseUser, maxRetries = 3): Promise<User | null> => {
    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        const token = await firebaseUser.getIdToken()
        const response = await fetch('https://us-central1-vapourvape-loyalty-prod.cloudfunctions.net/api/profile', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })
        
        if (response.ok) {
          const userData = await response.json()
          if (userData.type === 'user') {
            return userData.data
          }
        }
        
        // If not found and this is during signup, wait a bit and retry
        if (isSigningUp && attempt < maxRetries - 1) {
          console.log(`Profile not found, retrying... (attempt ${attempt + 1})`)
          await new Promise(resolve => setTimeout(resolve, 1000))
          continue
        }
        
        return null
      } catch (error) {
        console.error(`Error fetching user profile (attempt ${attempt + 1}):`, error)
        if (attempt < maxRetries - 1) {
          await new Promise(resolve => setTimeout(resolve, 1000))
        }
      }
    }
    return null
  }

  // Firebase auth state management (separate from staff sessions)
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setFirebaseUser(firebaseUser)
      
      // Skip Firebase auth processing if staff is already logged in
      const staffSession = localStorage.getItem('staffSession')
      if (staffSession) {
        console.log('AuthContext: Staff session exists, skipping Firebase auth processing')
        return
      }
      
      if (firebaseUser) {
        // Only try to fetch profile if we're not in the middle of signup
        // During signup, the user state is set directly by the signup function
        if (!isSigningUp) {
          const userData = await fetchUserProfile(firebaseUser, 1) // Only 1 retry for regular auth
          if (userData) {
            setUser(userData)
            setStaff(null)
          } else {
            // If profile not found and not signing up, user might not exist
            console.warn('User profile not found and not signing up')
            setUser(null)
            setStaff(null)
          }
        }
      } else {
        // Only clear user/staff if no staff session exists
        if (!localStorage.getItem('staffSession')) {
          setUser(null)
          setStaff(null)
        }
      }
      
      // Only set loading to false if no staff session (staff session sets it immediately)
      if (!localStorage.getItem('staffSession')) {
        setLoading(false)
      }
    })

    return unsubscribe
  }, [isSigningUp])

  const signUp = async (userData: { name: string; email: string; phone?: string; optInMarketing?: boolean; password: string }) => {
    try {
      setIsSigningUp(true) // Prevent onAuthStateChanged from interfering
      
      // Create Firebase user
      const userCredential = await createUserWithEmailAndPassword(auth, userData.email, userData.password)
      
      // Send email verification using Firebase's built-in service
      await sendEmailVerification(userCredential.user)
      
      // Create user profile in backend and wait for the response
      const token = await userCredential.user.getIdToken()
      const response = await fetch('https://us-central1-vapourvape-loyalty-prod.cloudfunctions.net/api/signup', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: userData.name,
          email: userData.email,
          phone: userData.phone,
          optInMarketing: userData.optInMarketing || false
        })
      })

      if (response.ok) {
        // Get the actual user data with the correct code from backend
        const backendUserData = await response.json()
        setUser(backendUserData)
        console.log('Signup successful, user set with code:', backendUserData.code)
      } else {
        // If backend fails, try to fetch the profile (maybe it was created)
        const userData = await fetchUserProfile(userCredential.user, 5) // More retries for signup
        if (userData) {
          setUser(userData)
          console.log('Signup backend failed but profile found with code:', userData.code)
        } else {
          throw new Error('Failed to create user profile')
        }
      }

    } catch (error: any) {
      console.error('Signup error:', error)
      throw error
    } finally {
      setIsSigningUp(false) // Re-enable onAuthStateChanged
    }
  }

  const signIn = async (email: string, password: string) => {
    try {
      await signInWithEmailAndPassword(auth, email, password)
      // User state will be updated by onAuthStateChanged
    } catch (error: any) {
      console.error('Sign in error:', error)
      throw error
    }
  }

  const signInWithGoogle = async () => {
    try {
      setIsSigningUp(true) // Prevent onAuthStateChanged from interfering
      
      const provider = new GoogleAuthProvider()
      provider.addScope('email')
      provider.addScope('profile')
      
      const result = await signInWithPopup(auth, provider)
      
      // Create/update user profile in backend and wait for response
      const token = await result.user.getIdToken()
      const response = await fetch('https://us-central1-vapourvape-loyalty-prod.cloudfunctions.net/api/social-signin', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: result.user.displayName || result.user.email?.split('@')[0] || 'User',
          email: result.user.email,
          provider: 'google'
        })
      })

      if (response.ok) {
        // Get the actual user data with the correct code from backend
        const backendUserData = await response.json()
        setUser(backendUserData)
        console.log('Google signin successful, user set with code:', backendUserData.code)
      } else {
        // If backend fails, try to fetch the profile (maybe user already exists)
        const userData = await fetchUserProfile(result.user, 5) // More retries for signup
        if (userData) {
          setUser(userData)
          console.log('Google signin backend failed but profile found with code:', userData.code)
        } else {
          throw new Error('Failed to create or fetch user profile')
        }
      }

    } catch (error: any) {
      console.error('Google sign-in error:', error)
      throw error
    } finally {
      setIsSigningUp(false) // Re-enable onAuthStateChanged
    }
  }

  const staffSignIn = async (username: string, password: string) => {
    try {
      const response = await fetch('https://us-central1-vapourvape-loyalty-prod.cloudfunctions.net/api/staff/signin-simple', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ username, password })
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Invalid credentials')
      }

      const { staff: staffData, session: sessionData } = await response.json()

      // Store staff session in localStorage for persistence (remember login)
      localStorage.setItem('staffSession', JSON.stringify({ 
        staff: staffData, 
        session: sessionData,
        timestamp: Date.now()
      }))
      
      // Set staff state (no Firebase auth needed for simple approach)
      setStaff(staffData)
      setUser(null)
      
    } catch (error: any) {
      console.error('Staff sign-in error:', error)
      throw error
    }
  }

  const resetPassword = async (email: string) => {
    try {
      // Use Firebase's built-in email service
      await sendPasswordResetEmail(auth, email)
    } catch (error: any) {
      console.error('Password reset error:', error)
      throw error
    }
  }

  const sendEmailVerificationToUser = async () => {
    try {
      if (firebaseUser) {
        // Use Firebase's built-in email service
        await sendEmailVerification(firebaseUser)
      } else {
        throw new Error('No user is currently signed in')
      }
    } catch (error: any) {
      console.error('Email verification error:', error)
      throw error
    }
  }

  const signOut = async () => {
    try {
      // Clear staff session if signing out
      localStorage.removeItem('staffSession')
      setStaff(null)
      
      await firebaseSignOut(auth)
      // User state will be updated by onAuthStateChanged
    } catch (error: any) {
      console.error('Sign out error:', error)
      throw error
    }
  }

  const value = {
    user,
    staff,
    firebaseUser,
    loading,
    signUp,
    signIn,
    signInWithGoogle,
    signOut,
    staffSignIn,
    resetPassword,
    sendEmailVerification: sendEmailVerificationToUser
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}